import { AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { GetCatalogService } from 'app/core/services/catalog/get-catalog.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
    summarizePolicyConstraints,
    type SummarizePolicyOptions
} from '../../core/utils/offer-policy-summary';
import { Offer } from 'app/core/models/catalog-dto';
import { Asset } from '../../core/models/asset';
import { CommonModule } from '@angular/common';
import { Modal, Toast } from 'bootstrap';

type AssetWithOffers = Asset & { offers: Offer[] };
type FilterKey = 'connector' | 'contentType';

@Component({
    selector: 'app-catalog',
    standalone: true,
    imports: [ CommonModule, TranslateModule ],
    templateUrl: './catalog.component.html',
    styleUrls: ['./catalog.component.scss']
})

export class CatalogComponent implements AfterViewInit, OnInit {

    @ViewChild('catalogSearcher') catalogSearcher!: ElementRef<HTMLInputElement>;

    @ViewChild('infoAssetModal') infoAssetModalRef!: ElementRef;
    public infoAssetModal!: Modal;

    @ViewChild('toastElement', { static: true }) toastEl!: ElementRef;
    toastMessage: string = '';

    isLoading = false;
    showFilters = false;
    openDropdown: FilterKey | null = null;

    assets: AssetWithOffers[] = [];
    filteredAssets: AssetWithOffers[] = [];

    sortCriteria = '';
    sortDirection: 'asc' | 'desc' = 'asc';

    filters: Record<FilterKey, Set<string>> = {
        connector: new Set<string>(),
        contentType: new Set<string>()
    };

    selectedAssetJSON: string = '';

    constructor(
        private getCatalogService: GetCatalogService,
        private translate: TranslateService
    ) { }

    private policySummaryOptions(extra: SummarizePolicyOptions = {}): SummarizePolicyOptions {
        const t = (key: string, interp?: Record<string, string>) =>
            this.translate.instant(`CATALOG.ASSET.${key}`, interp);
        return {
            locale: this.translate.currentLang || 'es',
            labels: {
                timeRange: (start, end) => t('POLICY-TIME-RANGE', { start, end }),
                timeFrom: (date) => t('POLICY-TIME-FROM', { date }),
                timeUntil: (date) => t('POLICY-TIME-UNTIL', { date }),
                locationIsAnyOf: (values) => t('POLICY-LOCATION-ANY', { values }),
                locationEq: (value) => t('POLICY-LOCATION-EQ', { value }),
                generic: (left, op, right) => t('POLICY-GENERIC', { left, op, right })
            },
            ...extra
        };
    }

    ngOnInit(): void {
        this.getCatalog();
        this.filteredAssets = [...this.assets];
    }

    ngAfterViewInit(): void {
        this.infoAssetModal = new Modal(this.infoAssetModalRef.nativeElement);
    }

    // ---------------- FILTER UI ---------------- //

    toggleFilters(): void {
        this.showFilters = !this.showFilters;
        this.openDropdown = null;
    }

    toggleDropdown(key: FilterKey): void {
        this.openDropdown = this.openDropdown === key ? null : key;
    }

    onFilterToggle(
        key: FilterKey,
        value: string,
        event: MouseEvent
    ): void {
        event.stopPropagation();
        const set = this.filters[key];
        set.has(value) ? set.delete(value) : set.add(value);
        this.applyAllFilters();
    }

    // ---------------- FILTER LOGIC ---------------- //

    applyAllFilters(): void {
        this.filteredAssets = this.assets.filter(asset => {
            return Object.entries(this.filters).every(([key, values]) => {
                if (!values.size) return true;
                return values.has((asset as any)[key]);
            });
        });

        this.applySorting();
    }

    getUniqueValues(field: FilterKey): string[] {
        return Array.from(
            new Set(
                this.assets
                    .map(asset => (asset as any)[field])
                    .filter(Boolean)
            )
        );
    }

    // ---------------- SEARCH ---------------- //

    searchCatalog(): void {
        const term = this.catalogSearcher.nativeElement.value.toLowerCase();

        if (!term) {
            this.applyAllFilters();
            return;
        }

        this.filteredAssets = this.filteredAssets.filter(asset =>
            asset.ID.toLowerCase().includes(term) ||
            asset.name.toLowerCase().includes(term) ||
            asset.contentType.toLowerCase().includes(term) ||
            summarizePolicyConstraints(
                asset.offers,
                this.policySummaryOptions({ maxRightOperandLength: 400 })
            )
                .toLowerCase()
                .includes(term)
        );
    }

    // ---------------- SORT ---------------- //

    sortAssetsBy(criteria: string): void {
        this.sortDirection =
            this.sortCriteria === criteria && this.sortDirection === 'asc'
                ? 'desc'
                : 'asc';

        this.sortCriteria = criteria;
        this.applySorting();
    }

    applySorting(): void {
        if (!this.sortCriteria) return;

        this.filteredAssets.sort((a, b) => {
            const av = (a as any)[this.sortCriteria] ?? '';
            const bv = (b as any)[this.sortCriteria] ?? '';

            return this.sortDirection === 'asc'
                ? av.localeCompare(bv, undefined, { numeric: true })
                : bv.localeCompare(av, undefined, { numeric: true });
        });
    }

    // ---------------- CLICK OUTSIDE ---------------- //

    @HostListener('document:click', ['$event'])
    closeDropdown(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (!target.closest('.dropdown')) {
            this.openDropdown = null;
        }
    }

    // ---------------- CATALOG ---------------- //

    private getCatalog(): void {
        this.isLoading = true;
        this.getCatalogService.obtain$().subscribe({
            next: ({ assets }) => {
                this.assets = assets;
                this.filteredAssets = [...this.assets];
                this.isLoading = false;
            },
            error: (err) => {
                console.error('[CatalogComponent] Error:', err.message);
                this.showToast('Error al obtener el catálogo', 'danger');
                this.isLoading = false;
            }
        });
    }

    // ---------------- COPY TEXT ---------------- //

    public copyToClipboard(text: string): void {
        navigator.clipboard.writeText(text).then(() => {
            console.log('Copiado');
        }).catch(err => {
            console.error('Error al copiar: ', err);
        });
    }

    // ---------------- ASSET INFO ---------------- //

    public openInfoAsset(asset: AssetWithOffers): void {
        this.showInfoAssetModal(asset);
        this.infoAssetModal.show();
    }

    public showInfoAssetModal(asset: AssetWithOffers): void {
        this.selectedAssetJSON = JSON.stringify(asset.originalDTO, null, 2);
    }

    public policyConstraintsCell(asset: AssetWithOffers): string {
        const s = summarizePolicyConstraints(
            asset.offers,
            this.policySummaryOptions({
                maxLength: 160,
                maxRightOperandLength: 72
            })
        );
        return s || this.translate.instant('CATALOG.ASSET.POLICY-NONE');
    }

    public policyConstraintsTooltip(asset: AssetWithOffers): string {
        const s = summarizePolicyConstraints(
            asset.offers,
            this.policySummaryOptions({ maxRightOperandLength: 400 })
        );
        return s || this.translate.instant('CATALOG.ASSET.POLICY-NONE');
    }

    public closeInfoAsset(): void {
        this.selectedAssetJSON = '';
        this.infoAssetModal.hide();
    }

    // ---------------- TOAST ---------------- //
    
    public showToast(message: string, type: 'success' | 'danger' | 'warning' = 'success'): void {
        this.toastMessage = message;
        const el = this.toastEl.nativeElement as HTMLElement;
        el.classList.remove('text-bg-success', 'text-bg-danger', 'text-bg-warning');
        el.classList.add(`text-bg-${type}`);
        const bsToast = new Toast(el);
        bsToast.show();
    }

}